<template>
  <div class="base-search" :class="{ 'expanded': expanded }">
    <!-- Search Button -->
    <q-btn
      @click="toggleSearch"
      :class="{ 'toggle-btn': isOutlined, 'active': expanded }"
      :title="expanded ? closeTitle : openTitle"
      :icon="expanded ? 'close' : 'search'"
      round
      flat
    />

    <!-- Search Input -->
    <Transition name="search-slide">
      <div v-if="expanded" class="search-input-container">
        <div class="search-input-wrapper">
          <input 
            ref="searchInput"
            :value="localValue"
            @input="onInput"
            @keydown.enter="onEnter"
            @keydown.escape="close"
            @keydown.arrow-down.prevent="onArrowDown"
            @keydown.arrow-up.prevent="onArrowUp"
            @focus="onFocus"
            @blur="onBlur"
            :placeholder="placeholder"
            class="search-input"
            :class="{ 'loading': loading }"
          />
          
          <div v-if="loading" class="search-spinner">
            <div class="spinner"></div>
          </div>
        </div>

        <!-- Suggestions dropdown -->
        <div 
          v-if="showSuggestions" 
          class="search-suggestions"
        >
          <div
            v-for="(suggestion, index) in filteredSuggestions"
            :key="suggestion.id || index"
            class="suggestion-item"
            :class="{ 'highlighted': highlightedIndex === index }"
            @click="selectSuggestion(suggestion)"
            @mouseenter="suggestionsFocused = true"
            @mouseleave="suggestionsFocused = false"
          >
            <q-icon name="search" size="1rem" class="suggestion-icon" />
            <div class="suggestion-content">
              <div class="suggestion-text">{{ suggestion.text }}</div>
              <div v-if="suggestion.description" class="suggestion-description">
                {{ suggestion.description }}
              </div>
            </div>
          </div>
        </div>

        <!-- Slot za rezultate -->
        <slot name="results"></slot>
      </div>
    </Transition>
  </div>
</template>

<script>
export default {
  name: 'BaseSearch',
  
  props: {
    modelValue: { type: String, default: '' },
    placeholder: { type: String, default: 'Search...' },
    loading: { type: Boolean, default: false },
    openTitle: { type: String, default: 'Search' },
    closeTitle: { type: String, default: 'Close' },
    closeOnOutsideClick: { type: Boolean, default: true },
    clearOnClose: { type: Boolean, default: true },
    suggestions: { type: Array, default: () => [] },
    defaultSuggestionsCount: { type: Number, default: 3 },
    maxSuggestionsCount: { type: Number, default: 5 },
    minQueryLength: { type: Number, default: 0 },
    isOutlined: { type: Boolean, default: false }
  },

  emits: [
    'update:modelValue', 
    'search', 
    'open', 
    'close', 
    'focus', 
    'blur', 
    'navigate', 
    'input',
    'suggestion-selected'
  ],

  data() {
    return {
      expanded: false,
      justOpened: false,
      inputFocused: false,
      suggestionsFocused: false,
      highlightedIndex: -1,
      localValue: this.modelValue
    }
  },

  computed: {
    hasSuggestions() {
      return this.suggestions?.length > 0
    },

    showSuggestions() {
      return (this.inputFocused || this.suggestionsFocused) &&
             this.localValue.length >= this.minQueryLength &&
             this.filteredSuggestions.length > 0
    },

    filteredSuggestions() {
      if (!this.hasSuggestions) return []

      if (!this.localValue || this.localValue.length < this.minQueryLength) {
        return this.suggestions.slice(0, this.defaultSuggestionsCount)
      }
      
      const query = this.localValue.toLowerCase()
      return this.suggestions
        .filter(s => s.text?.toLowerCase().includes(query) || s.description?.toLowerCase().includes(query))
        .slice(0, this.maxSuggestionsCount)
    }
  },

  watch: {
    modelValue(val) {
      this.localValue = val
    }
  },

  mounted() {
    document.addEventListener('click', this.onOutsideClick)
    document.addEventListener('keydown', this.onGlobalKeydown)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.onOutsideClick)
    document.removeEventListener('keydown', this.onGlobalKeydown)
  },

  methods: {
    toggleSearch() {
      this.expanded ? this.close() : this.open()
    },

    open() {
      this.expanded = true
      this.justOpened = true
      this.highlightedIndex = -1
      this.$emit('open')
      this.$nextTick(() => {
        this.$refs.searchInput?.focus()
        setTimeout(() => { this.justOpened = false }, 100)
      })
    },

    close() {
      this.expanded = false
      this.justOpened = false
      this.inputFocused = false
      this.suggestionsFocused = false
      this.highlightedIndex = -1
      if (this.clearOnClose) {
        this.localValue = ''
        this.$emit('update:modelValue', '')
      }
      this.$emit('close')
    },

    onInput(event) {
      const value = event.target.value
      this.localValue = value
      this.highlightedIndex = -1
      this.$emit('update:modelValue', value)
      this.$emit('input', value)
    },

    onEnter() {
      if (this.highlightedIndex >= 0 && this.filteredSuggestions[this.highlightedIndex]) {
        this.selectSuggestion(this.filteredSuggestions[this.highlightedIndex])
        return
      }
      this.$emit('search', this.localValue)
    },

    onFocus() {
      this.inputFocused = true
      this.$emit('focus')
    },

    onBlur() {
      setTimeout(() => {
        if (!this.suggestionsFocused) {
          this.inputFocused = false
        }
      }, 150)
      this.$emit('blur')
    },

    onArrowDown() {
      if (this.hasSuggestions && this.filteredSuggestions.length > 0) {
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredSuggestions.length - 1)
      }
      this.$emit('navigate', 'down')
    },

    onArrowUp() {
      if (this.hasSuggestions && this.highlightedIndex > 0) {
        this.highlightedIndex--
      }
      this.$emit('navigate', 'up')
    },

    selectSuggestion(suggestion) {
      this.localValue = suggestion.text
      this.$emit('update:modelValue', suggestion.text)
      this.$emit('suggestion-selected', suggestion)
      this.$emit('search', suggestion.text)
      this.inputFocused = false
      this.suggestionsFocused = false
      this.highlightedIndex = -1
    },

    onOutsideClick(event) {
      if (event.target.closest('.search-suggestions')) return
      if (this.justOpened || !this.closeOnOutsideClick) return
      if (this.expanded && !this.$el.contains(event.target)) {
        this.close()
      }
    },

    onGlobalKeydown(event) {
      if (this.expanded && event.key === 'Escape') {
        this.close()
      }
    },

    // Public methods
    focus() {
      this.$refs.searchInput?.focus()
    },

    clear() {
      this.localValue = ''
      this.$emit('update:modelValue', '')
      this.$refs.searchInput?.focus()
    }
  }
}
</script>

<style lang="scss" scoped>
.base-search {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-direction: row-reverse;
}

// Input container
.search-input-container {
  display: flex;
  align-items: center;
  position: relative;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input {
  width: 100%;
  height: 2.5rem;
  padding: 0 2.5rem 0 0.75rem;
  border: 1px solid $grey-4;
  border-radius: var(--radius-md);
  font-size: 0.875rem;
  background: white;
  box-shadow: var(--shadow-md);
  transition: all 0.2s ease;
  color: $grey-9;

  &::placeholder {
    color: $grey-6;
  }

  &:focus {
    outline: none;
    border-color: $info;
    box-shadow: 0 0 0 3px rgba($info, 0.15), var(--shadow-md);
  }

  body.body--dark & {
    background: $dark;
    border-color: rgba(255, 255, 255, 0.1);
    color: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    &:focus {
      border-color: $info;
      box-shadow: 0 0 0 3px rgba($info, 0.2), 0 4px 12px rgba(0, 0, 0, 0.4);
    }
  }
}

// Spinner
.search-spinner {
  position: absolute;
  right: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
}

.spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid $grey-4;
  border-top-color: $info;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  body.body--dark & {
    border-color: rgba(255, 255, 255, 0.2);
    border-top-color: $info;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// Suggestions
.search-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid $grey-4;
  border-radius: var(--radius-md);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: var(--z-panel);
  max-height: 15.625rem;
  overflow-y: auto;
  margin-top: 0.25rem;

  body.body--dark & {
    background: $dark;
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
  }
}

.suggestion-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid $grey-3;
  transition: background-color (--transition-fast) ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover,
  &.highlighted {
    background: $grey-2;
  }

  body.body--dark & {
    border-bottom-color: rgba(255, 255, 255, 0.05);

    &:hover,
    &.highlighted {
      background: $dark-page;
    }
  }
}

.suggestion-icon {
  margin-right: 0.75rem;
  color: $grey-6;
  flex-shrink: 0;

  body.body--dark & {
    color: rgba(255, 255, 255, 0.6);
  }
}

.suggestion-content {
  flex: 1;
  min-width: 0;
}

.suggestion-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: $grey-9;
  margin-bottom: 0.125rem;

  body.body--dark & {
    color: white;
  }
}

.suggestion-description {
  font-size: 0.75rem;
  color: $grey-6;
  line-height: 1.3;

  body.body--dark & {
    color: rgba(255, 255, 255, 0.6);
  }
}

// Transitions
.search-slide-enter-active,
.search-slide-leave-active {
  transition: all 0.3s ease;
}

.search-slide-enter-from,
.search-slide-leave-to {
  opacity: 0;
  transform: translateX(1.25rem) scale(0.95);
}

// Responsive
@media (max-width: 48rem), (max-height: 48rem)  {
  .search-input {
    font-size: 1rem; // Veći font na mobilnim za bolji UX
  }
}
</style>